import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, GitBranch, Sparkles } from "lucide-react";
import { api } from "../utils/api";
import { useAuth } from "../contexts/AuthContext";
import ActivityTimeline from "../components/ActivityTimeline";
import ActionBar from "../components/ActionBar";
import AttachmentList from "../components/AttachmentList";
import AttachmentUpload from "../components/AttachmentUpload";
import CommentThread from "../components/CommentThread";
import FeedbackWidget from "../components/FeedbackWidget";
import ClosureHistory from "../components/ClosureHistory";
import ResolutionDialog from "../components/ResolutionDialog";
import AssignDialog from "../components/AssignDialog";

// v1.9: resolved/closed/canceled/reopened are NOT in raw-status dropdown.
// Those transitions go through the ResolutionDialog so notes are captured.
const STATUSES = ["logged", "assigned", "in_progress", "pending_user_info", "pending_vendor_info"];
const CLOSURE_DIALOG_CONFIG = {
  resolved: { title: "Resolve incident", actionLabel: "Resolve", fieldLabel: "Resolution notes", tone: "primary", required: true },
  closed:   { title: "Close incident",   actionLabel: "Close & Archive", fieldLabel: "Closure notes", tone: "primary", required: true },
  canceled: { title: "Cancel incident",  actionLabel: "Cancel incident", fieldLabel: "Reason for cancellation", tone: "danger", required: true },
  reopened: { title: "Reopen incident",  actionLabel: "Reopen", fieldLabel: "Reason for reopening", tone: "primary", required: true },
};

export default function IncidentDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user, hasRole } = useAuth();
  const [it, setIt] = useState(null);
  const [users, setUsers] = useState([]);
  const [newAttachments, setNewAttachments] = useState([]);
  const [dialogAction, setDialogAction] = useState(null); // 'resolved' | 'closed' | 'canceled' | 'reopened'
  // v2.2 #1: in-app Assign modal instead of native window.confirm()
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignSuggestion, setAssignSuggestion] = useState(null);
  const isIT = hasRole("admin", "it_team", "it_manager");

  async function load() {
    const { data } = await api.get(`/api/incidents/${id}`); setIt(data);
  }
  useEffect(() => {
    load();
    if (isIT) api.get("/api/users").then((r) => setUsers(r.data));
  }, [id]); // eslint-disable-line

  async function update(payload) {
    try {
      const { data } = await api.put(`/api/incidents/${id}`, payload);
      setIt(data);
      return data;
    } catch (e) {
      alert(e.response?.data?.detail || "Update failed");
      throw e;
    }
  }

  async function uploadAttachments() {
    if (!newAttachments.length) return;
    await update({ attachments: newAttachments });
    setNewAttachments([]);
  }

  // v2.2 #1: open the in-app Assign modal seeded with a workload suggestion
  async function openAssignDialog() {
    try {
      const { data } = await api.get(`/api/users/suggest-assignee?ticket_type=incident&category=${encodeURIComponent(it.category)}`);
      if (data.assignee) {
        setAssignSuggestion({
          ...data.assignee,
          open_count: data.open_count,
          category_familiarity: data.category_familiarity,
        });
      } else {
        setAssignSuggestion(null);
      }
    } catch {
      setAssignSuggestion(null);
    }
    setAssignOpen(true);
  }
  async function handleAssignSubmit(userId) {
    await update({ assigned_to_id: userId });
    setAssignOpen(false);
  }

  async function convertToProblem() {
    if (!window.confirm(`Create a Problem record from incident ${it.incident_number}?`)) return;
    try {
      const { data } = await api.post(`/api/incidents/${id}/convert-to-problem`);
      if (window.confirm(`Created ${data.problem_number}. Open it now?`)) nav(`/problems/${data.problem_id}`);
    } catch (e) { alert(e.response?.data?.detail || "Conversion failed"); }
  }

  async function handleDialogSubmit(notes) {
    if (!dialogAction) return;
    await update({ status: dialogAction, resolution_notes: notes });
    setDialogAction(null);
  }

  if (!it) return <div className="text-gray-500">Loading…</div>;

  // ---- Action verbs (workflow doc v1.4 + v1.9 notes-on-closure) ----
  const actions = [];
  if (isIT) {
    actions.push({ key: "assign", label: "Assign", title: "Pick from suggested assignee", primary: true, onClick: openAssignDialog });
    if (["resolved", "closed"].includes(it.status)) {
      actions.push({ key: "reopen", label: "Reopen", onClick: () => setDialogAction("reopened") });
    } else {
      if (it.status !== "in_progress")
        actions.push({ key: "modify", label: "Mark In-Progress", onClick: () => update({ status: "in_progress" }) });
      actions.push({ key: "complete", label: "Resolve", primary: true, onClick: () => setDialogAction("resolved") });
      actions.push({ key: "close", label: "Close", onClick: () => setDialogAction("closed") });
      actions.push({ key: "cancel", label: "Cancel", danger: true, onClick: () => setDialogAction("canceled") });
    }
    actions.push({ key: "convert", label: "Create Problem", icon: GitBranch, onClick: convertToProblem });
  }

  const dlg = dialogAction ? CLOSURE_DIALOG_CONFIG[dialogAction] : null;
  // v2.0 #8: closed/resolved/canceled tickets cannot be reassigned. Hide assign UI.
  const isClosed = ["resolved", "closed", "canceled"].includes(it.status);

  return (
    <div className="space-y-4">
      <Link to="/incidents" className="text-sm text-brand-600 hover:underline flex items-center gap-1">
        <ArrowLeft size={14} /> Back to incidents
      </Link>
      <div className="card p-5">
        <div className="flex justify-between items-start">
          <div>
            <div className="text-xs font-mono text-gray-500">{it.incident_number}</div>
            <h1 className="text-xl font-bold">{it.title}</h1>
            <div className="text-sm text-gray-600 mt-1">
              Reporter: {it.reporter_name} · Plant: {it.plant} · Priority: <span className="font-semibold">{it.priority}</span>
            </div>
          </div>
          <span className="badge bg-brand-50 text-brand-700">{it.status}</span>
        </div>
        <p className="mt-4 text-sm whitespace-pre-wrap">{it.description}</p>
      </div>

      {actions.length > 0 && <ActionBar actions={actions} />}

      {isIT && (
        <div className="card p-5 space-y-3">
          <h2 className="font-semibold">Workflow</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="label flex items-center justify-between">
                <span>Assignee</span>
                {!isClosed && (
                  <button type="button" onClick={openAssignDialog} className="text-xs text-brand-600 hover:underline flex items-center gap-1">
                    <Sparkles size={12} /> Suggest
                  </button>
                )}
              </label>
              {isClosed ? (
                <div className="input bg-slate-50 text-slate-600 cursor-not-allowed">
                  {it.assigned_to_name || "— Unassigned —"}
                  <span className="ml-2 text-xs text-slate-500">(reopen to reassign)</span>
                </div>
              ) : (
                <select className="input" value={it.assigned_to_id || ""}
                  onChange={(e) => update({ assigned_to_id: e.target.value || null })}>
                  <option value="">— Unassigned —</option>
                  {users.filter((u) => ["it_team", "it_manager", "it_security", "admin"].includes(u.role))
                    .map((u) => <option key={u.id} value={u.id}>{u.full_name} ({u.role})</option>)}
                </select>
              )}
            </div>
            <div className="sm:col-span-2">
              <label className="label">Status (raw)</label>
              <select className="input" value={STATUSES.includes(it.status) ? it.status : ""}
                onChange={(e) => { if (e.target.value) update({ status: e.target.value }); }}>
                {!STATUSES.includes(it.status) && <option value="">{it.status} (closure — use action buttons)</option>}
                {STATUSES.map((s) => <option key={s}>{s}</option>)}
              </select>
              <p className="text-xs text-slate-500 mt-1">
                Resolve / Close / Cancel / Reopen require notes. Use the action buttons above — notes go into the permanent resolution history.
              </p>
            </div>
          </div>
        </div>
      )}

      {isIT && (
        <div className="card p-5 space-y-2">
          <h2 className="font-semibold">Add attachments</h2>
          <AttachmentUpload value={newAttachments} onChange={setNewAttachments} />
          {newAttachments.length > 0 && (
            <button className="btn-primary" onClick={uploadAttachments}>Upload {newAttachments.length} file(s)</button>
          )}
        </div>
      )}

      <AttachmentList items={it.attachments} />

      <ClosureHistory entries={it.closure_history || []} title="Resolution history" />

      <CommentThread kind="incidents" ticket={it} onPosted={load} />

      <FeedbackWidget kind="incidents" ticket={it} currentUser={user} onSubmitted={() => load()} />

      <div className="card p-5">
        <h2 className="font-semibold mb-3">Activity Timeline</h2>
        <ActivityTimeline entries={it.activity_log} />
      </div>

      {dlg && (
        <ResolutionDialog
          open={!!dialogAction}
          onClose={() => setDialogAction(null)}
          onSubmit={handleDialogSubmit}
          title={dlg.title}
          fieldLabel={dlg.fieldLabel}
          actionLabel={dlg.actionLabel}
          tone={dlg.tone}
          required={dlg.required}
        />
      )}

      <AssignDialog
        open={assignOpen}
        onClose={() => setAssignOpen(false)}
        onSubmit={handleAssignSubmit}
        suggested={assignSuggestion}
        users={users}
        allowedRoles={["it_team", "it_manager", "it_security", "admin"]}
        ticketCategory={it.category}
        ticketNumber={it.incident_number}
      />
    </div>
  );
}
